import { useContext, useEffect } from "react";
import { EditorContext } from "../containers/editor/EditorLayout";
import { customRound, floatToFloatSec, getHMSToSec, playMedia, stringTocamelCase } from "../_helpers/utils";
import { BRAND, COLLAGE, FRAME, STOCK, UPLOAD_VIDEO, VIDEO } from "../constants/editor";
import { EditorWrapperContext } from "../containers/editor/EditorWrapper";

let intervalId;

const animationSpeedToDurationMsObj = {
  Fast: 500,
  Medium: 1000,
  Slow: 2000,
};

const fadeEffectObj = {
  0: ["None", "", null, undefined],
  500: ["0.5 sec"],
  1000: ["1.0 sec", "1 sec"],
  2000: ["2.0 sec", "2 sec"],
};

const fadeIntervalObj = {
  500: [0, 100, 200, 300, 400, 500],
  1000: [0, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000],
  2000: [0, 200, 400, 600, 800, 1000, 1200, 1400, 1600, 1800, 2000],
};

const useDocumentPlayV2 = () => {
  const { audios, widgets, pageNodes, blockNodes, metadata, updateMetadata, isTimeLineViewOpen } =
    useContext(EditorContext);

  const { setPlayingPageIdx, whichPagePlaying, setWhichPagePlaying, documentPlayData, setDocumentPlayData } =
    useContext(EditorWrapperContext);

  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  {{   PAGE PLAY RELATED FUNCTIONS   }}  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////

  const getPageData = ({ timer, pageArray, pageIdx }) => {
    const targetPageObj =
      pageIdx >= 0 ? pageArray[pageIdx] : pageArray.find(p => timer >= p.pageStartTimeMs && timer <= p.pageStopTimeMs);

    const prevPageObj = pageArray?.[targetPageObj?.prevPageIdx];
    const nextPageObj = pageArray?.[targetPageObj?.nextPageIdx];

    return {
      pageId: targetPageObj?.pageId,
      pageIdx: targetPageObj?.pageIdx,
      pageObj: pageNodes?.[targetPageObj?.pageIdx],
      pageDurationMs: targetPageObj?.pageDurationMs,
      pageTimer: timer - targetPageObj?.pageStartTimeMs,
      prevPageId: prevPageObj?.pageId,
      prevPageIdx: prevPageObj?.pageIdx,
      prevPageBlockId: prevPageObj?.blockId,
      prevPageBlockIdx: prevPageObj?.blockIdx,
      nextPageId: nextPageObj?.pageId,
      nextPageIdx: nextPageObj?.pageIdx,
      nextPageIdBlockId: nextPageObj?.blockId,
      nextPageIdBlockIdx: nextPageObj?.blockIdx,
    };
  };

  const getWidgetData = ({ widgetObj, pageDurationMs }) => {
    const { startTime = 0, endTime = pageDurationMs } = widgetObj?.duration ?? {};
    const startTimeMs = parseFloat(startTime) * 1000;
    const endTimeMs = parseFloat(endTime) * 1000;
    const stopTimeMs = endTimeMs > pageDurationMs ? pageDurationMs : endTimeMs;
    const widgetNode = document.getElementById(widgetObj.id);
    const assetType = widgetObj.data["data-asset-type"];
    const source = widgetObj.data["data-source"];
    const frameContent = widgetObj.data["data-clipped"];

    return { startTimeMs, stopTimeMs, widgetNode, assetType, source, frameContent };
  };

  const getVideoNodeAndDataRef = ({ widgetId, widgetNode, videoNode }) => {
    const widget = widgetId ? document.getElementById(widgetId) : widgetNode;
    const assetTypeCollage = widget.dataset.assetType === COLLAGE;
    const video = assetTypeCollage ? videoNode : widget?.querySelector("video");
    const { duration: durationInHMS, volume, trim, loop } = assetTypeCollage ? video?.dataset : widget?.dataset;
    const durationInSec = getHMSToSec({ hms: durationInHMS });
    const trimArray = trim ? trim.split(",")?.map(e => parseFloat(e)) : null;
    const isTrimmed = Boolean(trimArray?.length === 2);
    const trimStart = isTrimmed ? trimArray?.[0] : 0;
    const trimEnd = isTrimmed ? trimArray?.[1] : durationInSec;

    return {
      widget,
      video,
      src: widget.dataset.assetType === FRAME ? video.querySelector("source")?.src : video.src,
      poster:
        widget.dataset.assetType === FRAME ? video.querySelector("source").getAttribute("data-poster") : video.poster,
      volume: volume ? parseInt(volume) : 100,
      durationInSec,
      durationInHMS,
      trimStart,
      trimEnd,
      isMuted: Boolean(video.getAttribute("muted")),
      isLooped: Boolean(loop === "true"),
      isTrimmed: Boolean(trimStart > 0 || trimEnd < durationInSec),
    };
  };

  const resumeDomPlayEnv = ({ timer, pageDurationMs, pageId }) => {
    // setting environment for resuming page play where left off (widget show/hide)
    widgets.forEach(w => {
      if (w.pageId === pageId && !w.data["data-layer-hidden"]) {
        const { startTimeMs, stopTimeMs, widgetNode } = getWidgetData({ widgetObj: w, pageDurationMs });
        widgetNode.style.display = timer >= startTimeMs && timer <= stopTimeMs ? "block" : "none";
      }
    });

    // setting environment for resuming page play where left off (page transition + widget animation)
    document.querySelectorAll(".play-animation").forEach(element => {
      element.style.animationPlayState = "";
    });
  };

  const setDomPlayEnvToCustomTime = ({ timer, pageDurationMs, pageObj, prevPageBlockId }) => {
    setPageTransitionCurTime({ timer, pageDurationMs, pageObj, prevPageBlockId });

    widgets.forEach(w => {
      if (w.pageId === pageObj.pageId && !w.data["data-layer-hidden"]) {
        const { startTimeMs, stopTimeMs, widgetNode, assetType, source, frameContent } = getWidgetData({
          widgetObj: w,
          pageDurationMs,
        });

        widgetNode.style.display = timer >= startTimeMs && timer <= stopTimeMs ? "block" : "none";

        if (assetType && startTimeMs < pageDurationMs) {
          setAnimationCurTime({ widgetNode, widgetObj: w, startTimeMs, stopTimeMs, timer });
        }

        if (assetType === VIDEO && [STOCK, BRAND, UPLOAD_VIDEO].includes(source) && startTimeMs < pageDurationMs) {
          setVideoCurTime({ widgetNode, timer, startTimeMs, stopTimeMs });
        }

        if (assetType === COLLAGE && startTimeMs < pageDurationMs) {
          widgetNode.querySelectorAll("video").forEach(videoNode => {
            setVideoCurTime({ widgetNode, videoNode, timer, startTimeMs, stopTimeMs });
          });
        }

        if (assetType === FRAME && frameContent === "video" && startTimeMs < pageDurationMs) {
          setVideoCurTime({ widgetNode, timer, startTimeMs, stopTimeMs });
        }
      }
    });
  };

  const stopDomPlay = () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    document.querySelectorAll(".play-animation").forEach(animationNode => {
      animationNode.style.animationPlayState = "paused";
    });
    document.querySelectorAll(`video[data-playing="true"], audio[data-playing="true"]`).forEach(mediaNode => {
      mediaNode.pause();
      mediaNode.removeAttribute("data-playing");
    });
  };

  const setVideoCurTime = ({ widgetNode, videoNode, timer, startTimeMs, stopTimeMs }) => {
    const {
      video,
      trimStart: trimStartTime,
      trimEnd: trimStopTime,
    } = getVideoNodeAndDataRef({ widgetNode, videoNode });

    if (timer > startTimeMs && timer < stopTimeMs) {
      // when widget is within playable time range
      const widgetPlayedDurationMs = timer - startTimeMs;
      const videoTrimmedDurationMs = (trimStopTime - trimStartTime) * 1000;
      const videoCurTimeWithinPlayableRange =
        (videoTrimmedDurationMs > widgetPlayedDurationMs
          ? widgetPlayedDurationMs
          : widgetPlayedDurationMs % videoTrimmedDurationMs) / 1000;

      video.currentTime = trimStartTime + videoCurTimeWithinPlayableRange;
    } else {
      video.currentTime = trimStartTime;
    }
  };

  const videoPlay = ({ widgetNode, videoNode, timer, startTimeMs, stopTimeMs }) => {
    const {
      video,
      volume,
      trimStart: trimStartTime,
      trimEnd: trimStopTime,
      isMuted,
      isLooped,
    } = getVideoNodeAndDataRef({ widgetNode, videoNode });

    if (video) {
      if (timer === startTimeMs) {
        if (isMuted) video.muted = false;
        video.currentTime = trimStartTime;
        video.volume = volume / 100;
        playMedia(video);
        video.setAttribute("data-playing", "true");
      }

      if (timer > startTimeMs && timer < stopTimeMs) {
        const videoCurTime = floatToFloatSec(video.currentTime);
        if (video.getAttribute("data-playing") !== "true") video.setAttribute("data-playing", "true");
        if (!(videoCurTime >= trimStartTime && videoCurTime <= trimStopTime)) {
          // when video is outside playable time range
          video.currentTime = trimStartTime;
          if (isLooped) {
            if (video.paused) playMedia(video);
          } else {
            video.pause();
          }
        } else {
          if (video.paused) playMedia(video);
        }
      }

      if (timer === stopTimeMs) {
        video.pause();
        if (isMuted) video.muted = true;
        video.removeAttribute("data-playing");
        video.currentTime = trimStartTime;
      }
    }
  };

  const setAnimationCurTime = ({ widgetNode, widgetObj, startTimeMs, stopTimeMs, timer }) => {
    const showEntryAnimation = widgetObj.data["data-entrance-animation"];
    const entryAnimationSpeed = widgetObj.data["data-entrance-animation-speed"];
    const entryAnimationType = widgetObj.data["data-entrance-animation-type"];
    const entryAnimationDurationMs = showEntryAnimation && animationSpeedToDurationMsObj[entryAnimationSpeed];
    const entryAnimationStopTime = showEntryAnimation && startTimeMs + entryAnimationDurationMs;
    const entryAnimationClassName = showEntryAnimation && stringTocamelCase(entryAnimationType);

    const showExitAnimation = widgetObj.data["data-exit-animation"];
    const exitAnimationSpeed = widgetObj.data["data-exit-animation-speed"];
    const exitAnimationType = widgetObj.data["data-exit-animation-type"];
    const exitAnimationDurationMs = showExitAnimation && animationSpeedToDurationMsObj[exitAnimationSpeed];
    const exitAnimationClassName = showExitAnimation && stringTocamelCase(exitAnimationType);
    const exitAnimationStartTime = showExitAnimation && stopTimeMs - exitAnimationDurationMs;

    if (!showEntryAnimation && !showExitAnimation) return;

    // detecting animation node
    let animationNode = widgetNode.querySelector(".animation-node");
    let widgetChildNodes = animationNode?.childNodes ?? widgetNode.childNodes;

    if (timer > startTimeMs && timer < stopTimeMs) {
      // when widget is within playable time range
      if (!animationNode) {
        // create animation node if not exists (scenario: playing from beginning)
        animationNode = document.createElement("div");
        animationNode.classList.add("animation-node");
        animationNode.style.height = "100%";
        animationNode.style.width = "100%";
        [...widgetChildNodes].forEach(child => animationNode.append(child));
        widgetNode.innerHTML = "";
        widgetNode.append(animationNode);
      }

      // set animation current time
      // entry animation playable range
      if (showEntryAnimation && timer >= startTimeMs && timer < entryAnimationStopTime) {
        const currentTimerDurationMs = timer - startTimeMs;
        const animationPlayedDurationMs = parseInt(
          animationNode.getAttribute("data-entry-animation-played-duration") ?? 0
        );

        // adding entry animation props
        animationNode.classList.add("play-animation");
        animationNode.classList.add(entryAnimationClassName);
        animationNode.style.animationDuration = `${entryAnimationDurationMs}ms`;
        animationNode.style.animationPlayState = "paused";
        animationNode.style.animationDelay =
          currentTimerDurationMs > animationPlayedDurationMs
            ? `-${currentTimerDurationMs - animationPlayedDurationMs}ms`
            : `${animationPlayedDurationMs - currentTimerDurationMs}ms`;

        // removing exit animation props
        animationNode.classList.remove(exitAnimationClassName);
        animationNode.removeAttribute("data-exit-animation-played-duration");
      }
      // exit animation playable range
      else if (showExitAnimation && timer >= exitAnimationStartTime && timer < stopTimeMs) {
        const currentTimerDurationMs = timer - exitAnimationStartTime;
        const animationPlayedDurationMs = parseInt(
          animationNode.getAttribute("data-exit-animation-played-duration") ?? 0
        );

        // adding exit animation props
        animationNode.classList.add("play-animation");
        animationNode.classList.add(exitAnimationClassName);
        animationNode.style.animationDuration = `${exitAnimationDurationMs}ms`;
        animationNode.style.animationPlayState = "paused";
        animationNode.style.animationDelay =
          currentTimerDurationMs > animationPlayedDurationMs
            ? `-${currentTimerDurationMs - animationPlayedDurationMs}ms`
            : `${animationPlayedDurationMs - currentTimerDurationMs}ms`;

        // removing entry animation props
        animationNode.classList.remove(entryAnimationClassName);
        animationNode.removeAttribute("data-entry-animation-played-duration");
      }
      // removing entry and exit animation props outside playable range
      else {
        animationNode.classList.remove("play-animation");
        animationNode.classList.remove(entryAnimationClassName);
        animationNode.classList.remove(exitAnimationClassName);
        animationNode.style.animationDuration = "";
        animationNode.style.animationPlayState = "";
        animationNode.style.animationDelay = "";
        animationNode.removeAttribute("data-entry-animation-played-duration");
        animationNode.removeAttribute("data-exit-animation-played-duration");
      }
    } else {
      if (animationNode) {
        // when widget is outside playable time range but animation props were added on last preview
        // re-assigning widgetChildNodes value as not working when force_stop
        widgetChildNodes = animationNode.childNodes;

        // replacing animation node with original node(s)
        widgetNode.innerHTML = "";
        [...widgetChildNodes].forEach(child => widgetNode.append(child));
      }
    }
  };

  const animationPlay = ({ widgetNode, widgetObj, startTimeMs, stopTimeMs, timer }) => {
    const showEntryAnimation = widgetObj.data["data-entrance-animation"];
    const entryAnimationSpeed = widgetObj.data["data-entrance-animation-speed"];
    const entryAnimationType = widgetObj.data["data-entrance-animation-type"];
    const entryAnimationDurationMs = showEntryAnimation && animationSpeedToDurationMsObj[entryAnimationSpeed];
    const entryAnimationStopTime = showEntryAnimation && startTimeMs + entryAnimationDurationMs;
    const entryAnimationClassName = showEntryAnimation && stringTocamelCase(entryAnimationType);

    const showExitAnimation = widgetObj.data["data-exit-animation"];
    const exitAnimationSpeed = widgetObj.data["data-exit-animation-speed"];
    const exitAnimationType = widgetObj.data["data-exit-animation-type"];
    const exitAnimationDurationMs = showExitAnimation && animationSpeedToDurationMsObj[exitAnimationSpeed];
    const exitAnimationClassName = showExitAnimation && stringTocamelCase(exitAnimationType);
    const exitAnimationStartTime = showExitAnimation && stopTimeMs - exitAnimationDurationMs;

    if (!showEntryAnimation && !showExitAnimation) return;

    // detecting animation node
    let animationNode = widgetNode.querySelector(".animation-node");
    let widgetChildNodes = animationNode?.childNodes ?? widgetNode.childNodes;

    if (!animationNode) {
      // create animation node if not exists (scenario: playing from beginning)
      animationNode = document.createElement("div");
      animationNode.classList.add("animation-node");
      animationNode.style.height = "100%";
      animationNode.style.width = "100%";
      [...widgetChildNodes].forEach(child => animationNode.append(child));
      widgetNode.innerHTML = "";
      widgetNode.append(animationNode);
    }

    // play / pause animations
    if (showEntryAnimation && timer === startTimeMs) {
      // entry animation start
      animationNode.classList.add("play-animation");
      animationNode.classList.add(entryAnimationClassName);
      animationNode.style.animationDuration = `${entryAnimationDurationMs}ms`;
    }

    if (showEntryAnimation && timer > startTimeMs && timer < entryAnimationStopTime) {
      animationNode.setAttribute("data-entry-animation-played-duration", timer);
    }

    if (showEntryAnimation && timer === entryAnimationStopTime) {
      // entry animation stop
      animationNode.classList.remove("play-animation");
      animationNode.classList.remove(entryAnimationClassName);
      animationNode.style.animationDuration = "";
      animationNode.style.animationPlayState = "";
      animationNode.style.animationDelay = "";
      animationNode.removeAttribute("data-entry-animation-played-duration");
    }

    if (showExitAnimation && timer === exitAnimationStartTime) {
      // exit animation start
      animationNode.classList.add("play-animation");
      animationNode.classList.add(exitAnimationClassName);
      animationNode.style.animationDuration = `${exitAnimationDurationMs}ms`;
    }

    if (showExitAnimation && timer > exitAnimationStartTime && timer < stopTimeMs) {
      animationNode.setAttribute("data-exit-animation-played-duration", timer - exitAnimationStartTime);
    }

    if (showExitAnimation && timer === stopTimeMs) {
      // exit animation stop
      animationNode.classList.remove("play-animation");
      animationNode.classList.remove(exitAnimationClassName);
      animationNode.style.animationDuration = "";
      animationNode.style.animationPlayState = "";
      animationNode.style.animationDelay = "";
      animationNode.removeAttribute("data-exit-animation-played-duration");
    }

    if (timer === stopTimeMs) {
      // widget/page duration end
      // re-assigning widgetChildNodes value as not working when force_stop
      widgetChildNodes = animationNode.childNodes;

      // replacing animation node with original node(s)
      widgetNode.innerHTML = "";
      [...widgetChildNodes].forEach(child => widgetNode.append(child));
    }
  };

  const widgetPlay = ({ timer, pageDurationMs, pageObj }) => {
    // widget play-pause
    widgets.forEach(w => {
      if (w.pageId === pageObj.pageId && !w.data["data-layer-hidden"]) {
        const { startTimeMs, stopTimeMs, widgetNode, assetType, source, frameContent } = getWidgetData({
          widgetObj: w,
          pageDurationMs,
        });

        if (!widgetNode) return;

        if (timer === startTimeMs) {
          widgetNode.style.display = "block";
        }

        if (assetType && startTimeMs < pageDurationMs) {
          animationPlay({ widgetNode, widgetObj: w, startTimeMs, stopTimeMs, timer });
        }

        if (assetType === VIDEO && [STOCK, BRAND, UPLOAD_VIDEO].includes(source) && startTimeMs < pageDurationMs) {
          videoPlay({ widgetNode, timer, startTimeMs, stopTimeMs });
        }

        if (assetType === COLLAGE && startTimeMs < pageDurationMs) {
          widgetNode.querySelectorAll("video").forEach(videoNode => {
            videoPlay({ widgetNode, videoNode, timer, startTimeMs, stopTimeMs });
          });
        }

        if (assetType === FRAME && frameContent === "video" && startTimeMs < pageDurationMs) {
          videoPlay({ widgetNode, timer, startTimeMs, stopTimeMs });
        }

        if (timer === stopTimeMs && stopTimeMs < pageDurationMs) {
          widgetNode.style.display = "none";
        }

        if (timer === pageDurationMs) {
          // page duration end
          widgetNode.style.display = "block";
        }
      }
    });
  };

  const setPageTransitionCurTime = ({ timer, pageDurationMs, pageObj, prevPageBlockId }) => {
    let wrapperNode;
    let animationNode;
    let backgroundNode;
    const pageNode = document.getElementById(pageObj.pageId);
    const blockNode = pageNode.querySelector(".dhp-page-block");
    const showEntryAnimation = pageObj.pageTransition.enabled;
    const entryAnimationSpeed = pageObj.pageTransition.speed;
    const entryAnimationType = pageObj.pageTransition.type;
    const entryAnimationDurationMs = showEntryAnimation && animationSpeedToDurationMsObj[entryAnimationSpeed];
    const entryAnimationStopTime = showEntryAnimation && entryAnimationDurationMs;
    const entryAnimationClassName = showEntryAnimation && stringTocamelCase(entryAnimationType);

    // reset playing page props
    pageNode.classList.add("page-playing");
    blockNode.style.pointerEvents = "none";
    blockNode.classList.add("canvas-disabled");
    localStorage.setItem("widget.event.isActive", "true");
    setPlayingPageIdx(pageObj.pageIdx);

    if (!showEntryAnimation) return;

    if (timer > 0 && timer < pageDurationMs) {
      // when page is within playable time range
      // detecting or creating wrapper/animation node
      if (!blockNode.parentNode.parentNode.classList.contains("wrapper-node")) {
        animationNode = document.createElement("div");
        animationNode.classList.add("animation-node");

        wrapperNode = document.createElement("div");
        wrapperNode.classList.add("wrapper-node");
        wrapperNode.style.overflow = "hidden";
        wrapperNode.style.height = "100%";
        wrapperNode.style.width = "100%";
        wrapperNode.appendChild(animationNode);

        blockNode.replaceWith(wrapperNode);
        wrapperNode.childNodes[0].appendChild(blockNode);
      } else {
        wrapperNode = pageNode.querySelector(".wrapper-node");
        animationNode = pageNode.querySelector(".animation-node");
      }

      // detecting or creating background node to display in the transition background
      if (prevPageBlockId) {
        if (!pageNode.querySelector(".transition-background-node")) {
          const prevPageBlockNode = document.getElementById(prevPageBlockId);

          backgroundNode = document.createElement("div");
          backgroundNode.classList.add("transition-background-node");
          backgroundNode.innerHTML = prevPageBlockNode.innerHTML;
          backgroundNode.style.cssText = prevPageBlockNode.style.cssText;
          backgroundNode.style.position = "absolute";

          // regenerate duplicate ids
          regenerateDuplicateIds({ backgroundNode });

          // reset video thumb to end screen
          resetVideoThumbToEndScreen({ backgroundNode });

          // insert *before* target node
          wrapperNode.parentNode.insertBefore(backgroundNode, wrapperNode);
        } else {
          backgroundNode = pageNode.querySelector(".transition-background-node");
        }
      }

      // set page transition current time
      // page transition playable range
      if (showEntryAnimation && timer > 0 && timer < entryAnimationStopTime) {
        const currentTimerDurationMs = timer - 0;
        const animationPlayedDurationMs = parseInt(
          animationNode.getAttribute("data-page-transition-played-duration") ?? 0
        );

        animationNode.classList.add("play-animation");
        animationNode.classList.add(entryAnimationClassName);
        animationNode.style.animationDuration = `${entryAnimationDurationMs}ms`;
        animationNode.style.animationPlayState = "paused";
        animationNode.style.animationDelay =
          currentTimerDurationMs > animationPlayedDurationMs
            ? `-${currentTimerDurationMs - animationPlayedDurationMs}ms`
            : `${animationPlayedDurationMs - currentTimerDurationMs}ms`;
      }
      // removing page transition props outside playable range
      else {
        animationNode.classList.remove("play-animation");
        animationNode.classList.remove(entryAnimationClassName);
        animationNode.style.animationDuration = "";
        animationNode.style.animationPlayState = "";
        animationNode.style.animationDelay = "";
        animationNode.removeAttribute("data-page-transition-played-duration");
      }
    } else {
      if (blockNode.parentNode.parentNode.classList.contains("wrapper-node")) {
        // when page is outside playable time range but page transition props were added on last preview
        wrapperNode = pageNode.querySelector(".wrapper-node");

        // removing wrapper/animation node and background node (replacing with original node)
        wrapperNode.replaceWith(blockNode);
        if (backgroundNode) backgroundNode.remove();
      }
    }
  };

  const pageTransitionPlay = ({ timer, pageDurationMs, pageObj, prevPageBlockId }) => {
    const pageNode = document.getElementById(pageObj.pageId);
    const blockNode = pageNode.querySelector(".dhp-page-block");
    const showEntryAnimation = pageObj.pageTransition.enabled;
    const entryAnimationSpeed = pageObj.pageTransition.speed;
    const entryAnimationType = pageObj.pageTransition.type;
    const entryAnimationDurationMs = showEntryAnimation && animationSpeedToDurationMsObj[entryAnimationSpeed];
    const entryAnimationStopTime = showEntryAnimation && entryAnimationDurationMs;
    const entryAnimationClassName = showEntryAnimation && stringTocamelCase(entryAnimationType);

    if (!showEntryAnimation) return;

    // detecting or creating wrapper/animation node
    let wrapperNode;
    let animationNode;

    if (!blockNode.parentNode.parentNode.classList.contains("wrapper-node")) {
      animationNode = document.createElement("div");
      animationNode.classList.add("animation-node");

      wrapperNode = document.createElement("div");
      wrapperNode.classList.add("wrapper-node");
      wrapperNode.style.overflow = "hidden";
      wrapperNode.style.height = "100%";
      wrapperNode.style.width = "100%";
      wrapperNode.appendChild(animationNode);

      blockNode.replaceWith(wrapperNode);
      wrapperNode.childNodes[0].appendChild(blockNode);
    } else {
      wrapperNode = pageNode.querySelector(".wrapper-node");
      animationNode = pageNode.querySelector(".animation-node");
    }

    // detecting or creating background node to display in the transition background
    let backgroundNode;

    if (prevPageBlockId) {
      if (!pageNode.querySelector(".transition-background-node")) {
        const prevPageBlockNode = document.getElementById(prevPageBlockId);

        backgroundNode = document.createElement("div");
        backgroundNode.classList.add("transition-background-node");
        backgroundNode.innerHTML = prevPageBlockNode.innerHTML;
        backgroundNode.style.cssText = prevPageBlockNode.style.cssText;
        backgroundNode.style.position = "absolute";

        // regenerate duplicate ids
        regenerateDuplicateIds({ backgroundNode });

        // reset video thumb to end screen
        resetVideoThumbToEndScreen({ backgroundNode });

        // insert *before* target node
        wrapperNode.parentNode.insertBefore(backgroundNode, wrapperNode);
      } else {
        backgroundNode = pageNode.querySelector(".transition-background-node");
      }
    }

    // play / pause animations
    if (showEntryAnimation && timer === 0) {
      // page transition start
      animationNode.classList.add("play-animation");
      animationNode.classList.add(entryAnimationClassName);
      animationNode.style.animationDuration = `${entryAnimationDurationMs}ms`;
    }

    if (showEntryAnimation && timer > 0 && timer < entryAnimationStopTime) {
      animationNode.setAttribute("data-page-transition-played-duration", timer);
    }

    if (showEntryAnimation && timer === entryAnimationStopTime) {
      // page transition stop
      animationNode.classList.remove("play-animation");
      animationNode.classList.remove(entryAnimationClassName);
      animationNode.style.animationDuration = "";
      animationNode.style.animationPlayState = "";
      animationNode.style.animationDelay = "";
      animationNode.removeAttribute("data-page-transition-played-duration");
    }

    if (showEntryAnimation && timer === pageDurationMs) {
      // page duration end
      // removing wrapper/animation node and background node (replacing with original node)
      wrapperNode.replaceWith(blockNode);
      if (backgroundNode) backgroundNode.remove();
    }
  };

  const regenerateDuplicateIds = ({ backgroundNode }) => {
    backgroundNode.querySelectorAll(".dhp-root-widget").forEach(widget => {
      widget.setAttribute("id", `${widget.id}-copy`);
    });
  };

  const resetVideoThumbToEndScreen = ({ backgroundNode }) => {
    backgroundNode.querySelectorAll(".dhp-page-widget").forEach(widget => {
      const { assetType, source, clipped } = widget.dataset;
      if (assetType === VIDEO && [STOCK, BRAND, UPLOAD_VIDEO].includes(source)) {
        const { video, trimEnd: trimStopTime } = getVideoNodeAndDataRef({ widgetNode: widget });
        const videoSrc = video?.src?.split("#t=")?.[0];
        video.currentTime = trimStopTime;
        video.src = `${videoSrc}#t=${trimStopTime}`;
      }
      if (assetType === FRAME && clipped === "video") {
        const { video, trimEnd: trimStopTime } = getVideoNodeAndDataRef({ widgetNode: widget });
        const videoSrc = video.querySelector("source")?.src?.split("#t=")?.[0];
        video.currentTime = trimStopTime;
        video.querySelector("source").src = `${videoSrc}#t=${trimStopTime}`;
      }
    });
  };

  const pagePlay = ({ timer, pageDurationMs, pageObj, prevPageBlockId }) => {
    const pageNode = document.getElementById(pageObj.pageId);
    const blockNode = pageNode.querySelector(".dhp-page-block");

    if (timer === 0) {
      // reset environments ready for page play
      updateCanvasControllerPageNo({ pageNo: pageObj.pageIdx + 1 });
      hideInactivePages({ activePageId: pageObj.pageId });
      setPlayingPageIdx(pageObj.pageIdx);

      // page play start
      pageNode.classList.add("page-playing");
      blockNode.style.pointerEvents = "none";
      blockNode.classList.add("canvas-disabled");
      pageTransitionPlay({ timer, pageDurationMs, pageObj, prevPageBlockId });
      localStorage.setItem("widget.event.isActive", "true");
    }

    if (timer === pageDurationMs) {
      // page play stop
      pageNode.classList.remove("page-playing");
      blockNode.style.pointerEvents = "";
      blockNode.classList.remove("canvas-disabled");
      pageTransitionPlay({ timer, pageDurationMs, pageObj, prevPageBlockId });
      localStorage.removeItem("widget.event.isActive");
    }
  };

  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  {{ DOCUMENT PLAY RELATED FUNCTIONS }}  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////

  const getDocumentData = () => {
    let pageArray = [];
    let documentDurationMs = 0;

    pageNodes.forEach((pageObj, pageIdx) => {
      const blockObj = blockNodes[pageIdx];
      const pageDurationMs = parseFloat(pageObj.pageDuration) * 1000;
      const pageStartTimeMs = documentDurationMs;
      const pageStopTimeMs = pageStartTimeMs + pageDurationMs;
      const prevPageIdx = pageNodes?.[pageIdx - 1]?.pageIdx;
      const nextPageIdx = pageNodes?.[pageIdx + 1]?.pageIdx;

      documentDurationMs += pageDurationMs;
      pageArray = [
        ...pageArray,
        {
          pageId: pageObj.pageId,
          pageIdx: pageIdx,
          blockId: blockObj.blockId,
          blockIdx: pageIdx,
          pageDurationMs,
          pageStartTimeMs,
          pageStopTimeMs,
          prevPageIdx: prevPageIdx ?? null,
          nextPageIdx: nextPageIdx ?? null,
        },
      ];
    });

    return {
      documentDurationMs,
      firstPageObj: pageArray[0],
      pageArray,
      scrollData: getScrollData(),
    };
  };

  const getScrollData = () => {
    const scrollContainer = document.getElementById("timeline-body");
    const count = Math.ceil(scrollContainer.scrollWidth / scrollContainer.offsetWidth);
    const breakPoints = [...Array(count)].map((_, i) => scrollContainer.offsetWidth * i);
    const rangePoints = breakPoints.map((p, i) => {
      const nextPoint = breakPoints[i + 1];
      return {
        min: p,
        max: nextPoint ? nextPoint - 1 : scrollContainer.scrollWidth,
      };
    });

    return { rangePoints };
  };

  const updateCanvasControllerPageNo = ({ pageNo }) => {
    const displayNode = document.querySelector(".canvas-controller .current-page");
    if (displayNode) displayNode.innerText = pageNo;
  };

  const hideInactivePages = ({ activePageId }) => {
    document.querySelectorAll(".dhp-page-canvas").forEach(page => {
      page.style.display = page.id === activePageId ? "block" : "none";
    });
  };

  const disableAssetPane = () => {
    const assetWrapper = document.querySelector(".dhp-secondAside .assets-wrapper");
    if (assetWrapper) assetWrapper.style.display = "none";
    // handleWidgetAction("*", false, false, 0, "returnToWidgetList");
  };

  const resetTimelineScrollPosition = () => {
    const scrollContainer = document.getElementById("timeline-body");
    const playHead = document.querySelector(".document-timeline-playhead");

    if (!scrollContainer) return;
    if (playHead) {
      // play initiated after pause
      const left = parseInt(playHead.style.left);
      scrollContainer.scrollLeft = left;
      scrollContainer.dataset.scrollLeft = left;
    } else {
      // new play
      scrollContainer.scroll(0, 0);
      scrollContainer.dataset.scrollLeft = 0;
    }
  };

  // send target page to a spacic playing state in pause mode
  const setTargetPagePlayToCustomTime = ({ documentTimer, documentProgress, targetPageObj }) => {
    setDocumentPlayData({
      ...documentPlayData,
      action: "pause",
      pageId: targetPageObj.pageId,
      pageIdx: targetPageObj.pageIdx,
      documentTimer: documentTimer - (documentTimer % 100), // documentTimer may come as 550ms, so making it divisible by 100 and remainder should be 0  (result => 550ms becomes 500ms)
      documentProgress,
    });
    setWhichPagePlaying({
      action: "pause",
      playingPageIdx: targetPageObj.pageIdx,
      triggerPoint: "slider",
    });
  };

  // update document-play as per playhead new position
  const documentPlayHeadHandler = ({ transX, scaleWidth }) => {
    const { documentDurationMs, pageArray } = documentPlayData;
    const documentDuration = documentDurationMs / 1000;
    const trackWidth = documentDuration * scaleWidth;
    const documentProgress = Math.round((transX * 100) / trackWidth);

    if (documentProgress >= 1 && documentProgress <= 99) {
      const documentTimer = (documentProgress * documentDurationMs) / 100;
      const targetPageObj = pageArray.find(
        p => documentTimer >= p.pageStartTimeMs && documentTimer <= p.pageStopTimeMs
      );

      // if playhead new position is outside active-page/playing-page
      if (targetPageObj.pageIdx !== documentPlayData.pageIdx) {
        // reset environments ready for target page to play
        updateCanvasControllerPageNo({ pageNo: targetPageObj.pageIdx + 1 });
        hideInactivePages({ activePageId: targetPageObj.pageId });
        setPlayingPageIdx(targetPageObj.pageIdx);
      }

      // finally reset environments ready to play from new position
      setTargetPagePlayToCustomTime({ documentTimer, documentProgress, targetPageObj });
    } else {
      // exit play mode if progress is 0 or 100
      setWhichPagePlaying({
        action: "force_stop",
        playingPageIdx: documentPlayData.pageIdx,
      });
    }
  };

  const documentPlay = ({ timer, documentDurationMs, pageObj, pageArray, scrollData }) => {
    const actionObj = timer === documentDurationMs ? { action: "natural_stop" } : {};
    const progress = customRound((100 / documentDurationMs) * timer, 2);

    setDocumentPlayData({
      ...documentPlayData,
      ...actionObj,
      pageId: pageObj.pageId,
      pageIdx: pageObj.pageIdx,
      documentTimer: timer,
      documentProgress: progress,
      documentDurationMs,
      pageArray,
      scrollData,
    });
  };

  const stopDocumentPlay = ({ delay = 0, action } = {}) => {
    const { firstPageObj } = getDocumentData();
    const scrollContainer = document.getElementById("timeline-body");
    setTimeout(() => {
      if (scrollContainer) {
        scrollContainer.scrollLeft = 0;
        scrollContainer.dataset.scrollLeft = 0;
      }
      updateCanvasControllerPageNo({ pageNo: firstPageObj.pageIdx + 1 });
      hideInactivePages({ activePageId: firstPageObj.pageId });
      setDocumentPlayData({
        action: null,
        pageId: null,
        pageIdx: null,
        documentTimer: null,
        documentProgress: 0,
        documentDurationMs: 0,
        pageArray: [],
        scrollData: null,
      });
      setWhichPagePlaying(action === "natural_stop" ? { ...whichPagePlaying, action } : {});
      setPlayingPageIdx(null);
    }, delay);
  };

  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  {{   AUDIO PLAY RELATED FUNCTIONS  }}  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////

  const getAllAudioData = () => {
    const allAudioData = {};
    audios?.forEach(audioObj => {
      const audioData = getAudioData(audioObj);
      allAudioData[audioObj.id] = audioData;
    });

    return { allAudioData };
  };

  const getAudioData = audioObj => {
    let fadeData = {};
    const audio = document.getElementById(`player-${audioObj.id}`);
    const durationInHMS = audioObj.data["data-duration"];
    const durationInSec = getHMSToSec({ hms: durationInHMS });

    const fadeInValue = audioObj.data["data-fade-in-value"];
    const fadeOutValue = audioObj.data["data-fade-out-value"];
    const fadeInMs = parseInt(Object.keys(fadeEffectObj).find(k => fadeEffectObj[k].includes(fadeInValue)));
    const fadeOutMs = parseInt(Object.keys(fadeEffectObj).find(k => fadeEffectObj[k].includes(fadeOutValue)));

    const trim = audioObj.data["data-trim"];
    const trimArray = trim ? trim.split(",")?.map(e => parseFloat(e)) : null;
    const isTrimmed = Boolean(trimArray?.length === 2);
    const trimStart = isTrimmed ? trimArray?.[0] : 0;
    const trimStop = isTrimmed ? trimArray?.[1] : durationInSec;

    const volume = parseInt(audioObj.data["data-volume"] ?? 100);
    const startTimeMs = parseFloat(audioObj.startTime) * 1000;
    const stopTimeMs = parseFloat(audioObj.endTime) * 1000;
    const trimStartTimeMs = trimStart * 1000;
    const trimStopTimeMs = trimStop * 1000;
    const trimmedDurationMs = trimStopTimeMs - trimStartTimeMs;

    const fadeInStartTimeMs = fadeInMs > 0 && startTimeMs;
    const fadeInStopTimeMs = fadeInMs > 0 && startTimeMs + fadeInMs;
    const fadeOutStartTimeMs = fadeOutMs > 0 && stopTimeMs - fadeOutMs;
    const fadeOutStopTimeMs = fadeOutMs > 0 && stopTimeMs;

    if (fadeInMs > 0 || fadeOutMs > 0) {
      fadeData = getFadeData({ volume, fadeInStartTimeMs, fadeInMs, fadeOutStartTimeMs, fadeOutMs });
    }

    return {
      audio,
      volume,
      startTimeMs,
      stopTimeMs,
      trimStartTimeMs,
      trimStopTimeMs,
      trimmedDurationMs,
      fadeInStartTimeMs,
      fadeInStopTimeMs,
      fadeOutStartTimeMs,
      fadeOutStopTimeMs,
      ...fadeData,
      isMuted: Boolean(volume === 0),
      isLooped: Boolean(stopTimeMs > trimStopTimeMs),
      isTrimmed: Boolean(trimStartTimeMs > 0 || trimStopTimeMs < stopTimeMs),
      isFadeIn: Boolean(fadeInMs > 0),
      isFadeOut: Boolean(fadeOutMs > 0),
    };
  };

  const getFadeData = ({ volume, fadeInStartTimeMs, fadeInMs, fadeOutStartTimeMs, fadeOutMs }) => {
    let fadeInIntervals = [];
    let fadeInVolumes = [];
    let fadeOutIntervals = [];
    let fadeOutVolumes = [];

    if (fadeInMs > 0) {
      const intervals = fadeIntervalObj[fadeInMs];
      const volumeFactor = volume / (intervals.length - 1);

      for (let i = 0; i < intervals.length; i++) {
        fadeInIntervals = [...fadeInIntervals, fadeInStartTimeMs + intervals[i]];

        if (i === 0) fadeInVolumes = [...fadeInVolumes, 0];
        if (i > 0 && i < intervals.length - 1) fadeInVolumes = [...fadeInVolumes, parseInt(i * volumeFactor)];
        if (i === intervals.length - 1) fadeInVolumes = [...fadeInVolumes, volume];
      }
    }

    if (fadeOutMs > 0) {
      const fadeoutIntervals = JSON.parse(JSON.stringify(fadeIntervalObj[fadeOutMs]));
      const intervals = fadeoutIntervals.reverse();
      const volumeFactor = volume / (intervals.length - 1);

      for (let i = intervals.length - 1; i >= 0; i--) {
        fadeOutIntervals = [...fadeOutIntervals, fadeOutStartTimeMs + intervals[i]];

        if (i === intervals.length - 1) fadeOutVolumes = [...fadeOutVolumes, volume];
        if (i > 0 && i < intervals.length - 1) fadeOutVolumes = [...fadeOutVolumes, parseInt(i * volumeFactor)];
        if (i === 0) fadeOutVolumes = [...fadeOutVolumes, 0];
      }
    }

    return { fadeInIntervals, fadeInVolumes, fadeOutIntervals, fadeOutVolumes };
  };

  const setAudioCurTime = ({ timer, allAudioData }) => {
    audios?.forEach(audioObj => {
      const { audio, trimStartTimeMs, trimStopTimeMs, startTimeMs, stopTimeMs } = allAudioData[audioObj.id];
      if (audio) {
        const trimStartTime = trimStartTimeMs / 1000;
        const trimStopTime = trimStopTimeMs / 1000;

        if (timer > startTimeMs && timer < stopTimeMs) {
          // when audio is within playable time range
          const audioPlayedDurationMs = timer - startTimeMs;
          const audioTrimmedDurationMs = (trimStopTime - trimStartTime) * 1000;
          const audioCurTimeWithinPlayableRange =
            (audioTrimmedDurationMs > audioPlayedDurationMs
              ? audioPlayedDurationMs
              : audioPlayedDurationMs % audioTrimmedDurationMs) / 1000;

          audio.currentTime = trimStartTime + audioCurTimeWithinPlayableRange;
        } else {
          audio.currentTime = trimStartTime;
        }
      }
    });
  };

  const audioPlay = ({ timer, documentDurationMs, allAudioData }) => {
    audios?.forEach(audioObj => {
      const {
        audio,
        volume,
        startTimeMs,
        stopTimeMs,
        trimStartTimeMs,
        trimStopTimeMs,
        fadeInIntervals,
        fadeInVolumes,
        fadeOutIntervals,
        fadeOutVolumes,
        isMuted,
        isLooped,
        isFadeIn,
        isFadeOut,
      } = allAudioData[audioObj.id];

      if (audio) {
        if (timer === startTimeMs) {
          if (isMuted) audio.muted = false;
          if (!isFadeIn) audio.volume = volume / 100;
          audio.currentTime = trimStartTimeMs / 1000;
          playMedia(audio);
          audio.setAttribute("data-playing", "true");
        }

        // fadeIn audio effect
        if (isFadeIn && fadeInIntervals.includes(timer)) {
          const idx = fadeInIntervals.indexOf(timer);
          audio.volume = fadeInVolumes[idx] / 100;
        }

        // fadeOut audio effect
        if (isFadeOut && fadeOutIntervals.includes(timer)) {
          const idx = fadeOutIntervals.indexOf(timer);
          audio.volume = fadeOutVolumes[idx] / 100;
        }

        if (timer > startTimeMs && timer < stopTimeMs) {
          const audioCurTimeMs = floatToFloatSec(audio.currentTime) * 1000;
          if (audio.getAttribute("data-playing") !== "true") audio.setAttribute("data-playing", "true");
          if (!(audioCurTimeMs >= trimStartTimeMs && audioCurTimeMs <= trimStopTimeMs)) {
            // when audio is outside playable time range
            audio.currentTime = trimStartTimeMs / 1000;
            if (isLooped) {
              if (audio.paused) playMedia(audio);
            } else {
              audio.pause();
            }
          } else {
            if (audio.paused) playMedia(audio);
          }
        }

        if ([stopTimeMs, documentDurationMs].includes(timer)) {
          audio.pause();
          if (isMuted) audio.muted = true;
          audio.removeAttribute("data-playing");
          audio.currentTime = trimStartTimeMs / 1000;
        }
      }
    });
  };

  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  {{          ALL USEEFFECTS         }}  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////
  ///////////////////////////////////////  -------------------------------------  ///////////////////////////////////////

  useEffect(() => {
    const action = documentPlayData.action;

    if (action === "play" && !intervalId) {
      let timer = documentPlayData?.documentTimer ?? 0;
      const { documentDurationMs, firstPageObj, pageArray, scrollData } = getDocumentData();
      const { pageId, pageIdx, pageObj, pageDurationMs, pageTimer, prevPageBlockId } = getPageData({
        timer,
        pageArray,
      });
      const { allAudioData } = getAllAudioData();

      if (!pageId || pageIdx < 0 || !pageObj) return;

      resetTimelineScrollPosition(); // reset scroll position to "default" OR "resume point"
      resumeDomPlayEnv({ timer: pageTimer, pageDurationMs, pageId }); // reset widget timelines and animations to "default" OR "resume point"

      if (timer === 0) {
        // reset environments ready for document play
        disableAssetPane();
        updateMetadata({
          ...metadata,
          activePageId: firstPageObj.pageId,
          activePageIdx: firstPageObj.pageIdx,
          activeBlockId: firstPageObj.blockId,
          activeBlockIdx: firstPageObj.blockIdx,
          activeWidgetId: false,
          activeWidgetType: false,
          activeOutSideCanvasArea: true, // show page dimension and hide page background
        });

        // document/page play start
        documentPlay({ timer: 0, documentDurationMs, pageObj, pageArray, scrollData });
        pagePlay({ timer: 0, pageDurationMs, pageObj, prevPageBlockId });
        widgetPlay({ timer: 0, pageDurationMs, pageObj });
        audioPlay({ timer: 0, allAudioData });
      }

      intervalId = setInterval(() => {
        // pageTimer is relative time for page which is derived from documentTimer (i.e. timer)
        timer += 100;
        const { pageId, pageIdx, pageObj, pageDurationMs, pageTimer, nextPageIdx, prevPageBlockId } = getPageData({
          timer,
          pageArray,
        });

        if (!pageId || pageIdx < 0 || !pageObj) return;

        // document/page play continues
        widgetPlay({ timer: pageTimer, pageDurationMs, pageObj });
        pagePlay({ timer: pageTimer, pageDurationMs, pageObj, prevPageBlockId });
        documentPlay({ timer, documentDurationMs, pageObj, pageArray, scrollData });
        audioPlay({ timer, allAudioData });

        // start next page play at the transition point i.e. previous page's stop time (initial 0-100ms of next page play starts from here then continues from above block)
        if (pageTimer === pageDurationMs && nextPageIdx) {
          const { pageId, pageObj, pageDurationMs, prevPageBlockId } = getPageData({ pageArray, pageIdx: nextPageIdx });
          resumeDomPlayEnv({ timer: 0, pageDurationMs, pageId }); // important! because for this page all widget timelines (mainly) and animations need to be reset to default (timer = 0)
          pagePlay({ timer: 0, pageDurationMs, pageObj, prevPageBlockId });
          widgetPlay({ timer: 0, pageDurationMs, pageObj });
        }
      }, 100);
    }

    if (action === "pause") {
      stopDomPlay();
    }

    if (action === "natural_stop") {
      stopDomPlay();
      stopDocumentPlay({ action });
    }

    if (action === "force_stop") {
      let timer = documentPlayData?.documentTimer ?? 0;
      const { documentDurationMs, pageArray } = getDocumentData();
      const { pageId, pageIdx, pageObj, pageDurationMs, prevPageBlockId } = getPageData({ timer, pageArray });
      const { allAudioData } = getAllAudioData();

      if (!pageId || pageIdx < 0 || !pageObj) return;

      widgetPlay({ timer: pageDurationMs, pageDurationMs, pageObj });
      pagePlay({ timer: pageDurationMs, pageDurationMs, pageObj, prevPageBlockId });
      audioPlay({ timer: documentDurationMs, documentDurationMs, allAudioData });
      stopDomPlay();
      stopDocumentPlay({ action });
    }

    return () => {
      stopDomPlay();
    };
  }, [documentPlayData.action]);

  useEffect(() => {
    if (
      whichPagePlaying.action === "pause" &&
      whichPagePlaying.triggerPoint === "slider" &&
      isTimeLineViewOpen === "document-timeline"
    ) {
      const { pageArray } = getDocumentData();
      const { allAudioData } = getAllAudioData();

      // determine last playingPageIdx
      const playingPageIdx = document.querySelector(".dhp-page-canvas.page-playing")?.getAttribute("data-page-idx");

      // if playhead new position is outside active-page/playing-page
      if (playingPageIdx !== documentPlayData.pageIdx) {
        // reset last page's environments ready to default (as if last page has completed playing 100% and set to default screen of that page)
        const { pageId, pageIdx, pageObj, pageDurationMs, prevPageBlockId } = getPageData({
          pageArray,
          pageIdx: playingPageIdx,
        });
        if (!pageId || pageIdx < 0 || !pageObj) return;

        widgetPlay({ timer: pageDurationMs, pageDurationMs, pageObj });
        pagePlay({ timer: pageDurationMs, pageDurationMs, pageObj, prevPageBlockId });
      }

      // setting environment ready to resume page play from new position
      const timer = documentPlayData?.documentTimer ?? 0;
      const { pageId, pageIdx, pageObj, pageDurationMs, pageTimer, prevPageBlockId } = getPageData({
        timer,
        pageArray,
      });
      if (!pageId || pageIdx < 0 || !pageObj) return;

      setDomPlayEnvToCustomTime({ timer: pageTimer, pageDurationMs, pageObj, prevPageBlockId });
      setAudioCurTime({ timer, allAudioData });
    }
  }, [whichPagePlaying]);

  return { documentPlayHeadHandler };
};

export default useDocumentPlayV2;
